// Strict types for Redux slices to ensure type safety

import type {
  MaternityNotificationResponseDto,
  MaternityRiskAssessmentResponseDto,
} from '@/types/Api';

// Helper type to ensure payload is defined
export type RequiredPayload<T> = T extends undefined ? never : T;

// Type guard functions for runtime validation
export function isMaternityNotificationArray(
  payload: unknown,
): payload is MaternityNotificationResponseDto[] {
  return (
    Array.isArray(payload) &&
    payload.every(
      (item) => typeof item === 'object' && item !== null && 'id' in item && 'employee' in item,
    )
  );
}

export function isMaternityNotification(
  payload: unknown,
): payload is MaternityNotificationResponseDto {
  return (
    typeof payload === 'object' && payload !== null && 'id' in payload && 'employee' in payload
  );
}

export function isMaternityAssessmentArray(
  payload: unknown,
): payload is MaternityRiskAssessmentResponseDto[] {
  return (
    Array.isArray(payload) &&
    payload.every(
      (item) => typeof item === 'object' && item !== null && 'id' in item && 'notification' in item,
    )
  );
}

export function isMaternityAssessment(
  payload: unknown,
): payload is MaternityRiskAssessmentResponseDto {
  return (
    typeof payload === 'object' && payload !== null && 'id' in payload && 'notification' in payload
  );
}

// Immutable update helpers
export function updateItemInArray<T extends { id: string }>(
  array: T[],
  id: string,
  updates: Partial<T>,
): T[] {
  return array.map((item) => (item.id === id ? { ...item, ...updates } : item));
}

export function removeItemFromArray<T extends { id: string }>(array: T[], id: string): T[] {
  return array.filter((item) => item.id !== id);
}

export function addItemToArray<T>(array: T[], item: T, position: 'start' | 'end' = 'start'): T[] {
  return position === 'start' ? [item, ...array] : [...array, item];
}

// Type for async thunk error handling
export interface ThunkErrorResponse {
  message: string;
  code?: string;
  details?: unknown;
}

// Status type used across slices
export type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';
